import React, { type ReactNode } from "react";
import { UserAvatar } from "@/components/ui/user-avatar";
import { DataTable, type DataTableColumn, type DataTableServerPagination } from "@/components/ui/data-table";
import { ItineraryStatusBadge } from "./ItineraryStatusBadge";
import { ItineraryActions } from "./ItineraryActions";
import { formatTravelDates } from "@/components/utils/utils";
import { formatPlanAmount } from "@/lib/builder/utils/payment-format";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { ItineraryStatus } from "@/lib/builder/models/itinerary.model";
import { useI18n } from '@/hooks/useI18n';
import { useModal } from "@/contexts/ModalContext";
import { getStorageThumbnailUrl } from "@/utils/imageStorage";
import { ExternalAgentBadge } from "@/lib/itineraries/ExternalAgentBadge";

interface ItinerariesTableProps {
    itineraries: DashboardItinerary[];
    onEdit?: (itineraryId: string) => void;
    onDelete?: (itineraryId: string) => void;
    onDuplicate?: (itineraryId: string) => void;
    onViewTravelRequest?: (travelRequestId: string) => void;
    onViewItinerary?: (itineraryId: string) => void;
    onUpdateStatus?: (itineraryId: string, status: ItineraryStatus) => void;
    onShared?: () => void;
    isAdmin?: boolean;
    isTraveler?: boolean;
    userRole?: string;
    /** Le righe sono gia' la pagina corrente: pagina e totale li gestisce il chiamante. */
    serverPagination?: DataTableServerPagination;
    pageSize?: number;
    isLoading?: boolean;
    /** Vedi DataTable: rifa' l'animazione di entrata a ogni caricamento. */
    animationKey?: string | number;
    emptyState?: ReactNode;
}

/** Itinerari in tabella, con la stessa DataTable delle altre liste del planner. */
export const ItinerariesTable: React.FC<ItinerariesTableProps> = ({
    itineraries,
    onEdit,
    onDelete,
    onDuplicate,
    onViewTravelRequest,
    onViewItinerary,
    onUpdateStatus,
    onShared,
    isAdmin,
    isTraveler,
    userRole,
    serverPagination,
    pageSize,
    isLoading,
    animationKey,
    emptyState,
}) => {
    const { t } = useI18n();
    const { openModal } = useModal();

    // Sotto il titolo la controparte: il cliente vede chi segue il viaggio, chi ci
    // lavora vede il cliente. La colonna assegnatario la vedono tutti tranne
    // l'advisor, per cui sarebbe sempre se stesso.
    const isClient = isTraveler || userRole === 'traveler';
    const isAdvisor = userRole === 'travel_designer' || userRole === 'travel_advisor_member';
    const counterpartOf = (itinerary: DashboardItinerary) =>
        isClient
            ? itinerary.assignedAdvisor && {
                name: `${itinerary.assignedAdvisor.firstName} ${itinerary.assignedAdvisor.lastName}`.trim(),
                email: itinerary.assignedAdvisor.email,
                imageUrl: itinerary.assignedAdvisor.avatarUrl,
            }
            : {
                name: itinerary.contactFullName,
                email: itinerary.contactEmail,
                imageUrl: itinerary.travelRequest.contact.avatarUrl,
            };

    const columns: DataTableColumn<DashboardItinerary>[] = [
        {
            id: 'title',
            header: t('itinerary.title'),
            cell: (itinerary) => {
                const person = counterpartOf(itinerary);
                return (
                // max-w: con il layout automatico un titolo lungo allargherebbe la colonna.
                <div className="flex items-center gap-3 max-w-[320px]">
                    <div
                        className="w-10 h-10 rounded-md bg-gray-200 bg-cover bg-center flex-shrink-0"
                        style={{
                            backgroundImage: itinerary.image
                                ? `url(${getStorageThumbnailUrl(itinerary.image.url, { noTransform: true })})`
                                : undefined,
                        }}
                    />
                    <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                            {itinerary.title || t('itinerary.untitledItinerary')}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
                            {person ? (
                                <>
                                    <UserAvatar fullName={person.name} email={person.email} imageUrl={person.imageUrl} size="xs" />
                                    <span className="text-sm text-gray-500 truncate">{person.name}</span>
                                </>
                            ) : (
                                <span className="text-sm text-gray-400 truncate">{t('travelRequest.notAssigned')}</span>
                            )}
                            {/* Il badge parla del cliente: lo vede solo chi vede il cliente. */}
                            {!isClient && itinerary.isExternalAgent && <ExternalAgentBadge />}
                        </div>
                    </div>
                </div>
                );
            },
        },
        {
            id: 'dates',
            header: t('itinerary.startDate'),
            // Le date non vanno mai a capo: se manca spazio scorre la tabella.
            headClassName: 'whitespace-nowrap',
            cellClassName: 'text-gray-600 whitespace-nowrap',
            cell: (itinerary) => formatTravelDates(itinerary.startDate, itinerary.endDate),
        },
        {
            id: 'guests',
            header: t('itinerary.guestsTitle'),
            headClassName: 'w-[100px]',
            cellClassName: 'text-gray-600',
            // Ospiti dell'itinerario, altrimenti quelli della richiesta: la richiesta
            // creata dal consulente puo' non avere il numero.
            cell: (itinerary) => (
                <>
                    {itinerary.totalGuests || '—'}{' '}
                    {itinerary.totalGuests === 1 ? t('common.guest') : t('common.guests')}
                </>
            ),
        },
        {
            id: 'status',
            header: t('itinerary.status'),
            // w-px + nowrap: la colonna e' larga quanto il badge piu' lungo della pagina,
            // non una misura fissa (layout automatico, vedi tableClassName).
            headClassName: 'w-px whitespace-nowrap',
            // badgeStatus, non status: a viaggio confermato il badge distingue
            // In programma / In corso / Viaggio passato, come la vista a card.
            cell: (itinerary) => (
                <ItineraryStatusBadge
                    status={itinerary.badgeStatus}
                    userRole={userRole}
                    detail={itinerary.installmentsProgressLabel}
                />
            ),
        },
        ...(!isAdvisor
            ? [{
                id: 'designer',
                header: t('travelRequest.designer'),
                cell: (itinerary: DashboardItinerary) => itinerary.assignedAdvisor ? (
                    <div className="flex items-center gap-2 min-w-0">
                        <UserAvatar
                            firstName={itinerary.assignedAdvisor.firstName}
                            lastName={itinerary.assignedAdvisor.lastName}
                            email={itinerary.assignedAdvisor.email}
                            imageUrl={itinerary.assignedAdvisor.avatarUrl}
                            size="sm"
                        />
                        <span className="text-gray-900 truncate max-w-[180px]">
                            {itinerary.assignedAdvisor.firstName} {itinerary.assignedAdvisor.lastName}
                        </span>
                    </div>
                ) : (
                    <span className="text-sm text-gray-400">{t('travelRequest.notAssigned')}</span>
                ),
            }]
            : []),
        // Ultima prima delle azioni: il totale dell'itinerario, formattato come nel piano rate.
        {
            id: 'price',
            header: t('modals.price'),
            headClassName: 'w-[140px] text-right',
            cellClassName: 'text-right font-medium text-gray-900 tabular-nums',
            // Sotto il totale la quota a persona, sugli stessi ospiti della colonna Ospiti.
            cell: (itinerary: DashboardItinerary) => (
                <>
                    <div>{formatPlanAmount(itinerary.total)}</div>
                    {itinerary.total != null && itinerary.totalGuests > 1 && (
                        <div className="text-xs font-normal text-gray-500">
                            {formatPlanAmount(itinerary.total / itinerary.totalGuests)} {t('itinerary.perPerson')}
                        </div>
                    )}
                </>
            ),
        },
    ];

    return (
        <DataTable<DashboardItinerary>
            data={itineraries}
            columns={columns}
            getRowId={(itinerary) => itinerary.id}
            onRowClick={(itinerary) =>
                openModal('itinerary-details', {
                    itinerary,
                    onViewItinerary: (id: string) => onViewItinerary?.(id),
                    // Stesse azioni del menu della riga, come pulsanti nell'anteprima
                    actions: {
                        onEdit, onDelete, onDuplicate, onViewTravelRequest, onViewItinerary,
                        onUpdateStatus, onShared, isAdmin, isTraveler, userRole,
                    },
                })
            }
            actionsHeader={t('common.actions')}
            actionsHeadClassName="w-[110px]"
            renderActions={(itinerary) => (
                <ItineraryActions
                    itinerary={itinerary}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                    onViewTravelRequest={onViewTravelRequest}
                    onViewItinerary={onViewItinerary}
                    onUpdateStatus={onUpdateStatus}
                    onShared={onShared}
                    isAdmin={isAdmin}
                    isTraveler={isTraveler}
                    userRole={userRole}
                    variant="table"
                />
            )}
            serverPagination={serverPagination}
            pageSize={pageSize}
            isLoading={isLoading}
            animationKey={animationKey}
            emptyState={emptyState}
            // table-auto: le colonne seguono il contenuto (lo stato si allarga solo per i
            // badge lunghi); i testi lunghi hanno un max-w e si troncano.
            tableClassName="min-w-[900px] table-auto"
        />
    );
};
